import { useCallback, useEffect, useState } from "react";
import { RefreshCw, X, ChevronDown, ChevronUp } from "lucide-react";

const POLL_MS = 5 * 60 * 1000;
const SNOOZE_MS = 30 * 60 * 1000;

async function fetchVersion() {
  try {
    const r = await fetch(`/version.json?t=${Date.now()}`, { cache: "no-store" });
    const j = await r.json();
    return j?.version ? String(j.version) : null;
  } catch {
    return null;
  }
}

// Modul-weit (überlebt Remounts des App-Baums): Version beim ersten Laden dieser Seite
let baseVersion = null;
let newVersionSeen = false;
let snoozeUntil = 0;

const reloadNow = () => {
  if (typeof window.__novatusHeal === "function") window.__novatusHeal();
  else window.location.reload();
};

const CACHE_STEPS = [
  ["iPad / iPhone (Safari)", "Einstellungen → (Apps →) Safari → Erweitert → Websitedaten → nach „novatus“ suchen → Löschen. Als App auf dem Home-Bildschirm: Symbol löschen und über Safari → Teilen → „Zum Home-Bildschirm“ neu hinzufügen."],
  ["Android (Chrome)", "Chrome → ⋮ → Einstellungen → Website-Einstellungen → Alle Websites → Seite wählen → „Daten löschen“. Danach die App neu öffnen."],
  ["Mac", "Safari: ⌥ + ⌘ + E (Cache leeren), dann ⌘ + R. Chrome: ⌘ + ⇧ + R."],
  ["Windows", "Chrome / Edge: Strg + ⇧ + R oder Strg + F5."],
];

function CacheHelp() {
  return (
    <div className="mt-2 space-y-1.5 text-[12px] text-zinc-300 leading-snug" data-testid="update-cache-help">
      {CACHE_STEPS.map(([dev, txt]) => (
        <div key={dev}><b className="text-zinc-100">{dev}:</b> {txt}</div>
      ))}
    </div>
  );
}

// Hinweis „Neue Version verfügbar“ – vergleicht /version.json mit dem Stand beim App-Start.
export function UpdateBanner() {
  const [available, setAvailable] = useState(newVersionSeen);
  const [snoozedUntil, setSnoozedState] = useState(snoozeUntil);
  const setSnoozedUntil = (t) => { snoozeUntil = t; setSnoozedState(t); };
  const [help, setHelp] = useState(false);

  const check = useCallback(async () => {
    const v = await fetchVersion();
    if (!v) return;
    if (!baseVersion) baseVersion = v;
    else if (v !== baseVersion) { newVersionSeen = true; setAvailable(true); }
  }, []);

  useEffect(() => {
    check();
    const id = setInterval(check, POLL_MS);
    const onVis = () => document.visibilityState === "visible" && check();
    document.addEventListener("visibilitychange", onVis);
    window.addEventListener("focus", check);
    return () => {
      clearInterval(id);
      document.removeEventListener("visibilitychange", onVis);
      window.removeEventListener("focus", check);
    };
  }, [check]);

  if (!available || Date.now() < snoozedUntil) return null;
  return (
    <div className="fixed inset-x-3 bottom-3 sm:left-auto sm:right-4 sm:bottom-4 sm:w-[400px] z-[90] rounded-2xl border border-[#00E575]/40 bg-[#0F1014]/95 backdrop-blur-xl shadow-2xl p-4"
         role="status" data-testid="update-banner">
      <div className="flex items-start gap-3">
        <div className="h-9 w-9 rounded-xl bg-[#00E575]/15 border border-[#00E575]/30 flex items-center justify-center shrink-0">
          <RefreshCw size={16} className="text-[#00E575]" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[14px] font-bold text-zinc-50">Neue Version verfügbar</div>
          <div className="text-[12.5px] text-zinc-400 mt-0.5">Bitte aktualisieren, damit du alle Neuerungen und Korrekturen nutzt.</div>
        </div>
        <button type="button" onClick={() => setSnoozedUntil(Date.now() + SNOOZE_MS)} data-testid="update-banner-close"
                className="h-7 w-7 rounded-lg text-zinc-500 hover:text-zinc-200 hover:bg-zinc-800 flex items-center justify-center" title="Später">
          <X size={15} />
        </button>
      </div>
      <div className="flex gap-2 mt-3">
        <button type="button" onClick={reloadNow} data-testid="update-banner-reload"
                className="h-10 flex-1 rounded-xl bg-[#00E575] text-[#09090B] font-bold text-[13.5px] hover:bg-[#00C964] active:scale-[0.98] transition-transform">
          Jetzt aktualisieren
        </button>
        <button type="button" onClick={() => setSnoozedUntil(Date.now() + SNOOZE_MS)} data-testid="update-banner-later"
                className="h-10 px-4 rounded-xl bg-zinc-900 border border-zinc-700 text-zinc-200 text-[13px] font-semibold hover:bg-zinc-800">
          Später
        </button>
      </div>
      <button type="button" onClick={() => setHelp(!help)} data-testid="update-banner-help-toggle"
              className="mt-2.5 text-[12px] text-zinc-400 hover:text-zinc-200 inline-flex items-center gap-1">
        {help ? <ChevronUp size={13} /> : <ChevronDown size={13} />} Hilft nicht? So leerst du den Cache manuell
      </button>
      {help && <CacheHelp />}
    </div>
  );
}
